import styled from "@emotion/styled";
import React, { useCallback, useMemo, useState } from "react";
import Text from "@components/Text";
import SizedBox from "@components/SizedBox";
import { Column, Row } from "@src/components/Flex";
import { useStores } from "@stores";
import Table from "@components/Table";
import SquareTokenIcon from "@components/SquareTokenIcon";
import BN from "@src/utils/BN";
import Button from "@components/Button";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "@src/constants";
import { useTheme } from "@emotion/react";
import { TPoolStats } from "@src/stores/LendStore";
import { ReactComponent as ArrowUp } from "@src/assets/icons/arrowUpCircle.svg";
import { useSupplyAndBorrowVM } from "./SupplyAndBorrowVM";
import Tooltip from "@components/Tooltip";

interface IProps {}

const Root = styled.div`
  display: flex;
  flex-direction: column;
`;

const TableWrap = styled.div<{ sort?: boolean }>`
  .sort-icon {
    width: 20px;
    height: 20px;
    margin-left: 4px;
  }

  .sort-icon-active {
    width: 20px;
    height: 20px;
    margin-left: 4px;
    transform: ${({ sort }) => (sort ? "scale(1)" : "scale(1, -1)")};
  }
`;

const AccountBorrowTable: React.FC<IProps> = () => {
  const { lendStore } = useStores();
  const [filteredBorrows, setFilteredBorrows] = useState<any[]>([]);
  const theme = useTheme();
  const vm = useSupplyAndBorrowVM();
  const navigate = useNavigate();

  const openModal = useCallback(
    (
      e: React.MouseEvent,
      marketId: string,
      operationName: string,
      assetId: string
    ) => {
      e.stopPropagation();
      return navigate(`/${marketId}/${operationName}/${assetId}`);
    },
    [navigate]
  );

  //-------------
  const borrowColumns = useMemo(
    () => [
      { Header: "Asset", accessor: "asset" },
      {
        Header: () => (
          <Row
            style={{ cursor: "pointer" }}
            onClick={() => vm.selectSort("selfBorrow", false)}
            justifyContent="flex-end"
          >
            <Text size="medium" fitContent nowrap>
              To be repaid
            </Text>
            <img
              src={theme.images.icons.group}
              alt="group"
              className={
                vm.sortBorrow === "selfBorrow"
                  ? "sort-icon-active"
                  : "sort-icon"
              }
            />
          </Row>
        ),
        accessor: "toRepay"
      },
      {
        Header: () => (
          <Row
            style={{ cursor: "pointer" }}
            onClick={() =>
              lendStore.accountBorrow.length > 1
                ? vm.selectSort("borrowAPY", false)
                : null
            }
            justifyContent="flex-end"
          >
            <Tooltip
              content={
                <Text textAlign="left">
                  Annual interest paid by borrowers taking into account
                  compounding.
                </Text>
              }
            >
              <Text size="medium" nowrap textAlign="right">
                Borrow APR
              </Text>
            </Tooltip>
            <img
              src={theme.images.icons.group}
              alt="group"
              className={
                vm.sortBorrow === "borrowAPY" ? "sort-icon-active" : "sort-icon"
              }
            />
          </Row>
        ),
        accessor: "borrowAPY"
      },
      {
        Header: () => (
          <Row
            style={{ cursor: "pointer" }}
            onClick={() =>
              lendStore.accountBorrow.length > 1
                ? vm.selectSort("dailyLoan", false)
                : null
            }
            justifyContent="flex-end"
          >
            <Text size="medium" nowrap textAlign="right">
              Daily loan interest
            </Text>
            <img
              src={theme.images.icons.group}
              alt="group"
              className={
                vm.sortBorrow === "dailyLoan" ? "sort-icon-active" : "sort-icon"
              }
            />
          </Row>
        ),
        accessor: "dailyLoan"
      },
      { Header: "", accessor: "emptyCell" },
      { Header: "", accessor: "emptyCell2" },
      { Header: "", accessor: "borrowBtn" },
      { Header: "", accessor: "repayBtn" }
    ],
    [vm, lendStore.accountBorrow.length, theme.images.icons.group]
  );
  useMemo(() => {
    const data = vm
      .sortData(lendStore.accountBorrow, false)
      .map((s: TPoolStats) => ({
        onClick: () =>
          navigate(
            ROUTES.MARKET_TOKEN_DETAILS.replace(
              ":marketId",
              lendStore.pool.address
            ).replace(":assetId", s.assetId)
          ),

        asset: (
          <Row alignItems="center">
            <SquareTokenIcon size="small" src={s.logo} alt="logo" />
            <SizedBox width={16} />
            <Column>
              <Text size="small" fitContent>
                {s.symbol}
              </Text>
              <Text type="secondary" size="small" fitContent>
                ${s.prices.max.toBigFormat(2)}
              </Text>
            </Column>
          </Row>
        ),
        toRepay: (
          <Column crossAxisSize="max">
            <Text weight={500} textAlign="right" size="medium">
              {BN.formatUnits(s.selfBorrow, s.decimals).toBigFormat(2) +
                ` ${s.symbol}`}
            </Text>
            <Text textAlign="right" size="small" type="secondary">
              ${" "}
              {BN.formatUnits(s.selfBorrow, s.decimals)
                .times(s.prices.min)
                .toBigFormat(2)}
            </Text>
          </Column>
        ),
        borrowAPY: s.borrowAPR.toBigFormat(2) + " %",
        dailyLoan: (
          <Column crossAxisSize="max">
            <Text weight={500} textAlign="right" size="medium">
              {BN.formatUnits(s.dailyLoan, s.decimals).toBigFormat(6) +
                ` ${s.symbol}`}
            </Text>
            <Text textAlign="right" size="small" type="secondary">
              ${" "}
              {BN.formatUnits(s.dailyLoan, s.decimals)
                .times(s.prices.min)
                .toBigFormat(6)}
            </Text>
          </Column>
        ),
        borrowBtn: (
          <Button
            kind="secondary"
            size="medium"
            fixed
            onClick={(e) =>
              openModal(e, lendStore.marketId, "borrow", s.assetId)
            }
            style={{ width: "100px", margin: "0 auto" }}
          >
            Borrow
          </Button>
        ),
        repayBtn: (
          <Button
            kind="secondary"
            size="medium"
            fixed
            onClick={(e) =>
              openModal(e, lendStore.marketId, "repay", s.assetId)
            }
            style={{ width: "100px", margin: "0 auto" }}
          >
            Repay
          </Button>
        )
      }));
    setFilteredBorrows(data);
  }, [
    vm,
    lendStore.accountBorrow,
    lendStore.pool.address,
    lendStore.marketId,
    navigate,
    openModal
  ]);
  //todo fix dependency problem
  return (
    <Root>
      {lendStore.accountBorrow.length > 0 && (
        <TableWrap sort={vm.sortModeBorrow === "descending"}>
          <Row alignItems="center">
            <Text weight={500} size="big" fitContent>
              My Borrow
            </Text>
            <SizedBox width={12} />
            <ArrowUp />
          </Row>
          <SizedBox height={8} />
          <Table columns={borrowColumns} data={filteredBorrows} />
        </TableWrap>
      )}
    </Root>
  );
};
export default observer(AccountBorrowTable);
